import React, { Component } from 'react';
import PropTypes from 'prop-types';

import { Tumbler } from './Tumbler';
import { TextField } from './TextField';
import { isEqual } from '../utils/compare';
import {
  moveItem,
  filterItems,
  getItemClasses,
  updateStateOfOptions,
  makeStateOfOptions,
  traverseState,
  normalizeValue,
} from './select/helpers';
import { SortableContainer } from './select/SortableContainer';
import { SortableItem } from './select/SortableItem';
import { WithErrorPlaceholderImage } from './select/WithErrorPlaceholderImage';
import { Button } from './Button';

import '../scss/_select.scss';

const PADDING_STEP = 20;

export class SelectList extends Component {
  constructor(props) {
    super(props);

    this.state = {
      search: '',
      tumblerIsActive: props.tumblerIsActive,
      withGroups: props.options.some(item => item.isGroupHeader),
      stateOfOptions: makeStateOfOptions(props.options, props.value, props.dnd),
      isSorting: false,
      tabsModeIsInclude: true,
    };
  }

  _selectList = null;

  shouldComponentUpdate(nextProps, nextState) {
    return !(isEqual([
      'value',
      'options',
      'isMulti',
      'isSearchable',
      'onChange',
      'tumbler',
      'dnd',
      'onTumblerChange',
      'labelImageSrc',
      'reactLabel',
      'className',
      'searchLabel',
      'searchPlaceholder',
      'getSearchInputHandler',
    ], this.props, nextProps) && isEqual([
      'search',
      'stateOfOptions',
      'tumblerIsActive',
      'targetDropIndex',
      'startDragIndex',
      'isSorting',
      'tabsModeIsInclude',
    ], this.state, nextState));
  }

  componentDidMount() {
    const { getSearchInputHandler } = this.props;
    if (getSearchInputHandler) {
      getSearchInputHandler(this.handleChangeSearch);
    }
    window.addEventListener('click', this.windowClickHandler);
  }

  componentDidUpdate(prevProps, prevState) {
    if (!isEqual(['value', 'options', 'isMulti', 'dnd'], this.props, prevProps) ||
      !isEqual(['isSorting'], this.state, prevState)) {
      this.setState({
        withGroups: this.props.options.some(item => item.isGroupHeader),
        stateOfOptions: makeStateOfOptions(this.props.options, this.props.value, this.props.dnd),
      });
    }
    if (prevProps.tumblerIsActive !== this.props.tumblerIsActive) {
      this.setState({ tumblerIsActive: this.props.tumblerIsActive });
    }
  }

  componentWillUnmount() {
    window.removeEventListener('click', this.windowClickHandler);
  }

  setStateOptions(state, item, fromDeselectedAll) {
    if (this.props.onChange) {
      let values = [];
      const callback = (item) => {
        if (item.isActive) values.push(item);
      };
      traverseState(state, callback);
      if (!this.props.isMulti) {
        values = values.length ? values[0] : null;
      }
      // add fromDeselectedAll for lazyLoad select
      this.props.onChange(normalizeValue(values), item, fromDeselectedAll);
    }
    this.setState({ stateOfOptions: state });
  }

  handleChangeSearch = (value) => {
    this.setState({ search: value });
  }

  toggleTumbler = () => {
    this.props.onTumblerChange();
  }

  handleSelectedOption = (item) => (e) => {
    const { isMulti, withTabs } = this.props;
    const { stateOfOptions, tabsModeIsInclude } = this.state;

    if (withTabs) {
      if (!item.isActive) {
        item = Object.assign(item, tabsModeIsInclude ? { included: true } : { excluded: true });
      }
    }

    const newState = updateStateOfOptions(stateOfOptions, item, isMulti);
    this.setStateOptions(newState, item);
  }

  onSortStart = () => {
    this.setState({ isSorting: true });
  };

  onSortEnd = ({ oldIndex, newIndex }) => {
    const newValues = moveItem(this.state.stateOfOptions, oldIndex, newIndex);
    this.setStateOptions(newValues);
    this.setState({
      targetDropIndex: null,
      startDragIndex: null,
      isSorting: false,
    });
  };

  renderItem = (item, i) => {
    const { isMulti, levelPadding, dnd, withTabs } = this.props;
    const { targetDropIndex, startDragIndex, tabsModeIsInclude } = this.state;

    let additionalProps = {
      style: {},
    };
    const padd = (item.deep || 0) * (levelPadding || PADDING_STEP);
    if (padd !== 0) {
      additionalProps.style.paddingLeft = padd;
    }

    if (!item.isGroupHeader && !item.alwaysEnabled) {
      additionalProps.onClick = this.handleSelectedOption(item);
    }

    // to make clickable group header bold the checking is needed:
    // - if option is not a child of other option and have its own children
    // - and also if no reactLabel is used since it has its own markup
    const isClickableGroupHeader = !item.reactLabel && (item.deep === 0) && item.options && item.options.length;

    const { isActive, draggableGroup, lastFirstGroup, included, excluded } = item;
    let isDisable = false;

    if (withTabs) {
      isDisable = tabsModeIsInclude ? !!excluded : !!included;
    }

    let className = getItemClasses(item, { isActive, isDisable });

    if (dnd && isActive && isMulti) {
      const indexItem = i;
      className = getItemClasses(item, { isActive, indexItem, startDragIndex, targetDropIndex });
    }

    if (isClickableGroupHeader) {
      className += ' _clickable-header';
    }

    let separator = lastFirstGroup ? <div className="_separator" /> : null;

    return dnd
      ? (
        <React.Fragment key={item.label ? item.label : i}>
          <SortableItem
            index={i}
            item={item}
            isActive={isActive}
            disabled={!isActive}
            collection={draggableGroup ? `${draggableGroup}` : isActive ? 'active' : 'disabled'}
            className={className}
            {...additionalProps} />
          {separator}
        </React.Fragment>
      )
      : (
        <div
          key={item.value !== undefined ? item.value : i}
          className={className}
          {...additionalProps}
        >
          <div className="select__option-label">
            {item.labelImageSrc &&
              <div className="select__option-image">
                <WithErrorPlaceholderImage src={item.labelImageSrc} />
              </div>
            }
            {item.reactLabel || item.label}
          </div>
        </div>
      );
  }

  renderList = (resultOptions) => {
    const { dnd } = this.props;

    const res = [];
    const cb = (item) => res.push(item);
    traverseState(resultOptions, cb);
    return dnd
      ? (
        <SortableContainer
          helperClass="select__dragged _active"
          distance={5}
          onSortStart={this.onSortStart}
          onSortEnd={this.onSortEnd}
          onAttachedRef={el => {
            this._selectList = el;
          }}
          items={res}>
          {res.map((item, i) => this.renderItem(item, i))}
        </SortableContainer>
      )
      : (
        <div className={`select__list`} ref={el => { this._selectList = el; }}>
          {res.map((item, i) => this.renderItem(item, i))}
        </div>
      );
  }

  renderButton = () => {
    const { buttonProps } = this.props;
    if (buttonProps) {
      return (
        <div className="w-100 d-flex justify-content-center">
          <Button className={buttonProps.className || 'btn-round _conflower-blue mt-3 mb-3'} onClick={buttonProps.onClick}>
            {!!buttonProps.symbolBeforeLabel && (
              <span className="btn-round__prefix">{buttonProps.symbolBeforeLabel}</span>
            )}
            {buttonProps.label || 'New'}
          </Button>
        </div>
      );
    }
    return null;
  }

  render() {
    const {
      className,
      isMulti,
      tumbler,
      tumblerLabel,
      dnd,
      isSearchable,
      changeSearchHandler,
      withTabs,
      searchLabel,
      searchPlaceholder,
      getSearchInputHandler,
      searchByValue,
    } = this.props;
    const { search, tumblerIsActive, withGroups, isSorting, stateOfOptions } = this.state;

    if (!stateOfOptions) {
      return null;
    }

    let resultOptions = stateOfOptions;
    if (isSearchable || getSearchInputHandler) {
      resultOptions = filterItems(stateOfOptions, search, searchByValue);
    }

    if (resultOptions.length === 0 && !isSearchable) {
      return null;
    }

    return (
      <div
        className={`select _opened js-select ${isSorting ? '_sorting' : ''} ${withGroups ? '_with-groups' : ''} ${className || ''} ${dnd ? '_dnd' : ''}`}
        ref={el => { this.select = el; }}
      >
        <div className="select__outer-wrapper">
          <div className="select__wrapper">
            <div className="select__list-wrapper">
              {withTabs && isMulti && this.renderTabs()}
              {isSearchable &&
                <div className="select__list-input-wrapper">
                  <TextField
                    iconName="Search"
                    // inputRef={el => { this.searchInput = el; }}
                    label={searchLabel}
                    placeholder={searchPlaceholder || 'Search'}
                    value={changeSearchHandler ? undefined : search}
                    onChange={changeSearchHandler || this.handleChangeSearch}
                    withBorder
                  />
                </div>
              }
              {this.renderButton()}
              {tumbler &&
                <Tumbler
                  className="select__tumbler"
                  onChange={this.toggleTumbler}
                  on={tumblerIsActive}
                  title={tumblerLabel || 'Show groups'}
                />
              }
              {this.renderList(resultOptions)}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

SelectList.propTypes = {
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  isMulti: PropTypes.bool,
  tumblerIsActive: PropTypes.bool,
  tumbler: PropTypes.bool,
  dnd: PropTypes.bool,
  onTumblerChange: PropTypes.func,
  reactLabel: PropTypes.elementType,
  labelImageSrc: PropTypes.string,
  tumblerLabel: PropTypes.string,
  isSearchable: PropTypes.bool,
  searchByValue: PropTypes.bool,
  className: PropTypes.string,
  levelPadding: PropTypes.number,
  changeSearchHandler: PropTypes.func,
  buttonProps: PropTypes.exact({
    label: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
    symbolBeforeLabel: PropTypes.string,
  }),
  withTabs: PropTypes.bool,
  searchLabel: PropTypes.string,
  searchPlaceholder: PropTypes.string,
  getSearchInputHandler: PropTypes.func,
};
